import "zod-openapi";
import { z } from "@reflag/shared/utils/zod";

import { AdminAPI } from "./adminAPI";
import { AIAPI } from "./aiAPI";
import { APIKeyAPI } from "./apiKeyAPI";
import { AppAPI } from "./appAPI";
import { BackgroundOperationAPI } from "./backgroundOperationAPI";
import { BootstrapAPI } from "./bootstrapAPI";
import { CodeSampleSandboxAPI } from "./codeSampleSandboxAPI";
import { CompanyAPI } from "./companyAPI";
import { DeprecatedCLIOAuthAPI } from "./deprecatedCLIOAuthAPI";
import { EnvironmentAPI } from "./environmentAPI";
import { EventAPI } from "./eventAPI";
import { FeatureAPI } from "./featureAPI";
import { FeatureConfigAPI } from "./featureConfigAPI";
import { FeatureFeedbackAPI } from "./featureFeedbackAPI";
import { FeatureTargetingAPI } from "./featureTargetingAPI";
import { FeatureViewAPI } from "./featureViewAPI";
import { FeedbackApi } from "./feedbackAPI";
import { FilterAPI } from "./filterAPI";
import { FlagAPI } from "./flagAPI";
import { GitHubConnectionAPI } from "./githubConnectionAPI";
import { ImageAPI } from "./imageAPI";
import { ImportAPI } from "./importAPI";
import { InviteAPI } from "./inviteAPI";
import { LinearConnectionAPI } from "./linearConnectionAPI";
import { OnboardingAPI } from "./onboardingAPI";
import { OrgAPI } from "./organizationAPI";
import { SegmentAPI } from "./segmentAPI";
import { SlackConnectionAPI } from "./slackConnectionAPI";
import { StageAPI } from "./stageAPI";
import { TargetingNotificationPolicyAPI } from "./targetingNotificationPolicyAPI";
import { UserAPI } from "./userAPI";

type API = BackgroundOperationAPI &
  AppAPI &
  FeatureAPI &
  FlagAPI &
  CompanyAPI &
  OrgAPI &
  BootstrapAPI &
  EventAPI &
  InviteAPI &
  SlackConnectionAPI &
  LinearConnectionAPI &
  FeatureViewAPI &
  FeatureConfigAPI &
  FeatureFeedbackAPI &
  FeatureTargetingAPI &
  FeedbackApi &
  SegmentAPI &
  UserAPI &
  AdminAPI &
  EnvironmentAPI &
  FilterAPI &
  StageAPI &
  ImageAPI &
  CodeSampleSandboxAPI &
  GitHubConnectionAPI &
  DeprecatedCLIOAuthAPI &
  AIAPI &
  APIKeyAPI &
  ImportAPI &
  OnboardingAPI &
  TargetingNotificationPolicyAPI;

export default API;

export type APIDefWithErrorResponses = {
  [TPath in keyof API]: {
    [TMethod in keyof API[TPath]]: API[TPath][TMethod] extends { response: any }
      ? Omit<API[TPath][TMethod], "response"> & {
          response: API[TPath][TMethod]["response"] | ErrorResponse;
        }
      : API[TPath][TMethod];
  };
};

export const ErrorCodeSchema = z
  .enum([
    "invalid_request",
    "not_found",
    "not_possible",
    "not_allowed",
    "not_available",
    "unknown_error",
    "unauthorized",
    "unauthenticated",
  ])
  .meta({
    description: "Error code",
  });

// Recursive schema for nested validation error structure
const ValidationIssuesSchema = z
  .record(
    z.string().meta({
      description:
        "The field that has the issue (uses dot notation). Empty string if the issue is at the root.",
    }),
    z
      .array(
        z.string().meta({
          description: "The error message",
        }),
      )
      .meta({
        description: "Error messages for this field",
      }),
  )
  .meta({
    description: "Validation issues, applicable if the request is invalid",
  });

export type ValidationIssues = z.infer<typeof ValidationIssuesSchema>;

export const ErrorResponseSchema = z
  .object({
    error: z
      .object({
        code: ErrorCodeSchema,
        message: z.string().meta({
          description: "Human readable error message",
        }),
      })
      .meta({
        description: "The error",
      }),
    issues: ValidationIssuesSchema.optional().meta({
      description: "Individual validation issues, if applicable",
    }),
  })
  .meta({
    description:
      "The error response, including individual issues, if applicable",
    id: "ErrorResponse",
  });

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

export type APIResponse<T> = T;
export type APIEmptyResponse = APIResponse<object>;

export type BulkDeleteResponse = APIResponse<{
  deletedIds: string[];
  notFoundIds: string[];
}>;

export type APICollectionResponse<TData extends Record<string, any>> = {
  data: Array<TData>;
};

export type APICollectionResponseWithMetadata<
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
> = {
  data: Array<TData>;
  metadata: TMetadata;
};

export class APIHandlerError extends Error {
  public status: number;
  public response: ErrorResponse;

  constructor(status: number, response: ErrorResponse) {
    super(response.error.message);

    this.name = "APIHandlerError";
    this.status = status;
    this.response = response;
  }
}

export const API_VERSION_HEADER = "x-reflag-version";
